import { createClient, Client } from 'graphql-ws';
import { RootState, store } from '@redux/store';
import {
  getTokenLocalStorage,
  getTokenSessionStorage,
  getOrganizationId,
} from '@config/storage';

/**
 * Standalone graphql-ws client for GraphQL subscriptions.
 *
 * The web app uses graphql-request + React Query (no Apollo), which cannot do
 * subscriptions. This lightweight client runs alongside them: subscription
 * events are cache signals that patch React Query, never a source of truth.
 * See _bmad-output/planning-artifacts/architecture.md.
 */

const buildWsUrl = (): string => {
  // Explicit override wins (should be a full ws(s):// endpoint incl. /graphql).
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  // Derive from the HTTP endpoint: http->ws, https->wss. Normalize a possible
  // trailing slash so it works whether or not REACT_APP_BACKEND_URL ends in '/'.
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  return base.replace(/^http/, 'ws') + '/graphql';
};

let client: Client | null = null;

/**
 * Lazily create (and reuse) the singleton ws client. `lazy` means the socket
 * only opens on the first subscription and closes when the last one ends.
 */
export const getWsClient = (): Client => {
  if (client) {
    return client;
  }
  client = createClient({
    url: buildWsUrl(),
    lazy: true,
    keepAlive: 10_000,
    retryAttempts: Infinity,
    // Use graphql-ws's default shouldRetry: transient closes reconnect, but
    // fatal handshake closes (4400/4401/4403 — bad request / unauthorized /
    // forbidden) stop, so a dead token or forbidden org doesn't hammer the
    // server forever.
    // Cap the reconnect backoff so a dropped/late connection recovers in ~2s
    // instead of graphql-ws's default exponential backoff climbing toward 20s+.
    retryWait: async (retries: number) => {
      const delay = Math.min(1000 * 2 ** retries, 2000) + Math.random() * 300;
      await new Promise((resolve) => setTimeout(resolve, delay));
    },
    // A function so the token/org are read fresh on every (re)connect — handles
    // token rotation on long-lived sockets.
    connectionParams: () => {
      const state: RootState = store.getState();
      const rememberMe = state.user?.rememberMe;
      const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
      const organizationId = getOrganizationId();
      return {
        // Shared WS auth contract: raw JWT (no "Bearer") + org header key.
        authorization: token,
        'x-organization-id': organizationId,
      };
    },
  });
  return client;
};

/**
 * Tear down the socket and drop the singleton (call on logout or org switch so
 * the next connection re-reads connectionParams with the new identity). Clears
 * the reference synchronously so a subsequent getWsClient() builds a fresh
 * client while the old one disposes in the background.
 */
export const disposeWsClient = (): void => {
  if (client) {
    const current = client;
    client = null;
    void current.dispose();
  }
};
